// NEXUS LUXE PRO - SIMPLE SCROLL TEXT REVEAL ENGINE
// Standalone version without external dependencies

document.addEventListener('DOMContentLoaded', function() {
  // Initialize all scroll text reveal sections
  const sections = document.querySelectorAll('.nexus-scroll-text-reveal');
  
  sections.forEach(section => {
    initializeScrollReveal(section);
  });
  
  // Mutation observer for dynamically added sections
  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const newSections = node.querySelectorAll ? 
            node.querySelectorAll('.nexus-scroll-text-reveal') : [];
          newSections.forEach(initializeScrollReveal);
        }
      });
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});

function initializeScrollReveal(section) {
  const textContainer = section.querySelector('[data-reveal-text]');
  if (!textContainer) return;
  
  // Process text content
  const text = textContainer.textContent;
  const words = text.split(/(\s+)/);
  
  // Clear existing content
  textContainer.innerHTML = '';
  
  // Create word elements
  words.forEach((word, index) => {
    if (word.trim() === '') {
      // Preserve whitespace
      const space = document.createTextNode(word);
      textContainer.appendChild(space);
    } else {
      // Create word span
      const wordSpan = document.createElement('span');
      wordSpan.className = 'reveal-word';
      wordSpan.textContent = word;
      wordSpan.dataset.wordIndex = index;
      wordSpan.style.opacity = '0.25';
      wordSpan.style.color = getComputedStyle(section).getPropertyValue('--muted-text-color') || 
                            getComputedStyle(textContainer).color.replace('rgb', 'rgba').replace(')', ', 0.25)');
      wordSpan.style.transition = 'opacity 0.1s cubic-bezier(0.22, 1, 0.36, 1), color 0.1s cubic-bezier(0.22, 1, 0.36, 1)';
      wordSpan.style.willChange = 'opacity, color';
      textContainer.appendChild(wordSpan);
    }
  });
  
  textContainer.classList.add('processed');
  textContainer.removeAttribute('data-loading');
  textContainer.setAttribute('data-loaded', 'true');
  
  // Add scroll listener
  let ticking = false;
  
  const handleScroll = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        updateRevealProgress(section, textContainer);
        ticking = false;
      });
      ticking = true;
    }
  };
  
  // Initial update
  updateRevealProgress(section, textContainer);
  
  // Add event listeners
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleScroll, { passive: true });
  
  // Store cleanup function
  section._cleanup = () => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleScroll);
  };
  
  // Handle section removal
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node === section || node.contains?.(section)) {
          if (section._cleanup) {
            section._cleanup();
          }
          observer.disconnect();
        }
      });
    });
  });
  
  observer.observe(document.body, { childList: true, subtree: true });
}

function updateRevealProgress(section, textContainer) {
  const rect = section.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  
  // Calculate scroll progress (0 to 1)
  const progress = Math.max(0, Math.min(1, 
    (viewportHeight - rect.top) / (viewportHeight + rect.height)
  ));
  
  // Get all word elements
  const wordElements = textContainer.querySelectorAll('.reveal-word');
  const totalWords = wordElements.length;
  
  if (totalWords === 0) return;
  
  // Calculate how many words should be revealed
  const wordsToReveal = Math.floor(progress * totalWords);
  const fractionalProgress = (progress * totalWords) % 1;
  
  // Get colors from CSS variables or computed styles
  const textColor = getComputedStyle(section).getPropertyValue('--text-color') || 
                   getComputedStyle(textContainer).color;
  const mutedColor = getComputedStyle(section).getPropertyValue('--muted-text-color') || 
                    textColor.replace('rgb', 'rgba').replace(')', ', 0.25)');
  
  // Update word states
  wordElements.forEach((word, index) => {
    if (index < wordsToReveal) {
      // Fully revealed
      word.style.opacity = '1';
      word.style.color = textColor;
    } else if (index === wordsToReveal && fractionalProgress > 0) {
      // Partially revealed (transition state)
      const opacity = 0.25 + (fractionalProgress * 0.75);
      word.style.opacity = opacity.toString();
      word.style.color = interpolateColor(mutedColor, textColor, fractionalProgress);
    } else {
      // Muted state
      word.style.opacity = '0.25';
      word.style.color = mutedColor;
    }
  });
  
  // Update progress indicator if exists
  const progressIndicator = section.querySelector('.progress-indicator');
  if (progressIndicator) {
    const progressValue = progressIndicator.querySelector('.progress-value');
    if (progressValue) {
      const percent = Math.round(progress * 100);
      progressValue.textContent = `${percent}%`;
      
      if (progress > 0 && progress < 1) {
        progressIndicator.classList.add('visible');
      } else {
        progressIndicator.classList.remove('visible');
      }
    }
  }
}

function interpolateColor(startColor, endColor, progress) {
  // Parse RGBA colors
  const parseRGBA = (color) => {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (match) {
      return {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3]),
        a: match[4] ? parseFloat(match[4]) : 1
      };
    }
    return { r: 255, g: 255, b: 255, a: 1 };
  };
  
  const start = parseRGBA(startColor);
  const end = parseRGBA(endColor);
  
  const r = Math.round(start.r + (end.r - start.r) * progress);
  const g = Math.round(start.g + (end.g - start.g) * progress);
  const b = Math.round(start.b + (end.b - start.b) * progress);
  const a = start.a + (end.a - start.a) * progress;
  
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// Handle page visibility changes
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    // Refresh all sections when page becomes visible
    const sections = document.querySelectorAll('.nexus-scroll-text-reveal');
    sections.forEach(section => {
      const textContainer = section.querySelector('[data-reveal-text]');
      if (textContainer) {
        updateRevealProgress(section, textContainer);
      }
    });
  }
});

// Handle reduced motion preference
const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
motionQuery.addEventListener('change', (e) => {
  const sections = document.querySelectorAll('.nexus-scroll-text-reveal');
  sections.forEach(section => {
    const textContainer = section.querySelector('[data-reveal-text]');
    if (textContainer) {
      if (e.matches) {
        // Disable animations
        const words = textContainer.querySelectorAll('.reveal-word');
        words.forEach(word => {
          word.style.transition = 'none';
          word.style.opacity = '1';
          word.style.color = getComputedStyle(section).getPropertyValue('--text-color') || 
                           getComputedStyle(textContainer).color;
        });
      } else {
        // Re-enable animations
        const words = textContainer.querySelectorAll('.reveal-word');
        words.forEach(word => {
          word.style.transition = 'opacity 0.1s cubic-bezier(0.22, 1, 0.36, 1), color 0.1s cubic-bezier(0.22, 1, 0.36, 1)';
        });
        updateRevealProgress(section, textContainer);
      }
    }
  });
});